import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { addNewAddress } from '@kfc-global/react-shared/redux/Actions/OrderservAction';
import { resetStores } from '@kfc-global/react-shared/redux/Actions/StoresAction';
import geoLocation from '@kfc-global/react-shared/utils/geoLocation';
import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import {
  getAndCheckIfOneTrue,
  getCheckIfAnyOnePass,
  getTernaryValue,
  getValueOrEmptyString,
  isEmptyObject,
  toastifyFn,
} from 'common/utilities/utils';
import { ADDRESS_STORE_CONSTANTS, FORM_HEADING } from 'organisms/AccountPage/AU/Constants/Constants';
import AddressModal from 'organisms/AddressModel/Presentation/AddressModal';
import { DELIVERY_OPTION_DATA, NULL } from 'common/constants/SharedConstants';
import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import AuthManager from '@kfc-global/react-shared/auth/AuthManager';
import { setAddressDetails } from 'organisms/ContactUs/Utils/contactUsUtils';
import { getSortedSuggesstion } from 'organisms/SearchStore/AU/Presentation/DispositionModuleUtils/DispositionModuleUtils';

export const floorValidation = addressObj => {
  const { addressRegex, valueInfo, setSaveAddress, addresses, name } = addressObj;
  const valid = addressRegex?.test(valueInfo);
  if (valid) {
    const addressLines = [valueInfo, addresses?.addressLines[1]];
    setSaveAddress({ ...addresses, [name]: valueInfo, addressLines: addressLines, isFlatNumberSelected: false });
  }
};

const AddressModalContainer = props => {
  const dispatch = useDispatch();
  const { modalType, toggleModal, updateSetOrderDetails } = props;
  const { HIDDEN, DELIVERY_SERVICE, CHANNEL, INITIAL_STATE, MAX_DELIVERY_DISTANCE, NO_OF_STORE_FOR_DELIVERY } =
    ADDRESS_STORE_CONSTANTS;
  const { FIELD_NAME } = FORM_HEADING;
  const { ADDRESS_UPDATED, SAVED_DELIVERY_ADDRESS } = TRANSLATE_MAPPING_KEY;

  const { allStores } = useSelector(data => data.storesReducer);
  const { savedAddresses } = useSelector(data => data.profileReducer);
  const tenantReducer = useSelector(data => data.tenantReducer);
  const { tenantId = '', storeSearch = {}, country, countryNameInDefaultLang, componentOptions } = tenantReducer;
  const shouldEnableConfirmButton =
    componentOptions?.web?.dispositionConfig?.DELIVERY_STORES?.shouldEnableConfirmButton;
  const address = getTernaryValue(isEmptyObject(props?.address), props?.address, INITIAL_STATE);
  const [addresses, setSaveAddress] = useState(address);
  const [error, setError] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const geohashprecision = storeSearch?.geohashprecision;
  const boundingBoxSize = storeSearch?.boundingBoxSizeMeters / 1000;
  const [isNameExist, setIsNameExist] = useState(false);
  const [isInvalidName, setIsInvalidName] = useState(false);
  const filterConditions = tenantReducer?.componentOptions?.web?.accountPage?.filterConditions;
  const addressRegex = tenantReducer?.addressRegex;
  const [nonFormattedAddress, setNonFormattedAddress] = useState();

  const { ready, value, suggestions, setValue, clearSuggestions } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: {
        country: process?.env?.REACT_APP_TENANT_CODE,
      },
      types: tenantReducer?.componentOptions?.web?.accountPage?.filteredTypes,
      /* Define search scope here */
    },
    debounce: 300,
  });

  const suggestionsData = {
    ...suggestions,
    data: getSortedSuggesstion(suggestions?.data, filterConditions),
  };
  const suggestionRef = useRef(null);

  const handleClose = () => {
    document.querySelector('body').style.overflow = '';
    toggleModal(false);
    dispatch(resetStores());
  };

  useEffect(() => {
    if (modalType) {
      geoLocation.calculateGH({
        lat: parseFloat(getAndCheckIfOneTrue(addresses?.location?.latitude, addresses?.latitude)),
        lon: parseFloat(getAndCheckIfOneTrue(addresses?.location?.longitude, addresses?.longitude)),
        distanceFromCurrentLoc: boundingBoxSize,
        geoHashPrecision: geohashprecision,
      });
    }
    document.querySelector('body').style.overflow = HIDDEN;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  useEffect(() => {
    modalType
      ? setSaveAddress(prevState => ({
          ...prevState,
          isAddressSelected: true,
        }))
      : setSaveAddress(prevState => ({
          ...prevState,
          isAddressSelected: false,
        }));
  }, [modalType]);

  const handleClickOutside = event => {
    if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
      clearSuggestions();
    }
  };

  const handleInput = event => {
    if (!addresses?.isAddressSelected) {
      setValue(event.target.value);
      const addressLines = [addresses?.addressLines[0], event.target.value];
      setSaveAddress({ ...addresses, addressLines: addressLines });
    }
    setShowSuggestions(true);
  };

  const checkIfNameExist = valueInfo => {
    const addressArray = getTernaryValue(
      !modalType,
      savedAddresses,
      savedAddresses.filter(addr => addr?.id !== address?.id),
    );
    return !!addressArray.find(
      addressInfo => addressInfo?.name.toUpperCase()?.trim() === valueInfo.toUpperCase().trim(),
    );
  };

  const handleChange = ({ target }) => {
    const { name, value: valueInfo } = target;
    const addressNameValue = getTernaryValue(name === FIELD_NAME.NAME, valueInfo, addresses.name);
    if (name === FIELD_NAME.DELIVERY_OPTION) {
      setSaveAddress({ ...addresses, [name]: valueInfo !== DELIVERY_OPTION_DATA[0] ? valueInfo : '' });
      return;
    }

    const addressObj = { addressRegex, valueInfo, setSaveAddress, addresses, name };
    if (addressNameValue !== '') {
      const alreadyExist = checkIfNameExist(addressNameValue);
      const valid = addressRegex?.test(addressNameValue);
      if (!valid) {
        setIsNameExist(false);
        setIsInvalidName(true);
      } else if (alreadyExist) {
        setIsNameExist(true);
        setIsInvalidName(false);
      } else {
        setIsInvalidName(false);
        setIsNameExist(false);
      }
    } else {
      setIsInvalidName(false);
      setIsNameExist(false);
    }

    if (name === FIELD_NAME.FLOOR) {
      floorValidation(addressObj);
      return;
    }

    if (target.type !== FIELD_NAME.CHECKBOX) {
      setSaveAddress({ ...addresses, [name]: valueInfo });
    } else {
      const isDefaultAvailable = savedAddresses.find(addr => addr.isPrimary && addr.id === addresses.id);
      if (!isDefaultAvailable) {
        setSaveAddress({ ...addresses, [name]: valueInfo !== 'true' });
      }
    }
  };

  const handleSelect =
    ({ description, structured_formatting: { main_text, secondary_text } }) =>
    () => {
      // When user selects a place, we can replace the keyword without request data from API
      // by setting the second parameter to "false"
      // setValue(main_text, false);
      setShowSuggestions(false);
      setError(false);
      clearSuggestions();
      dispatch(resetStores());
      // Get latitude and longitude via utility functions
      getGeocode({
        address: description,
        componentRestrictions: {
          country: process?.env?.REACT_APP_TENANT_CODE,
        },
      })
        .then(results => {
          setAddressDetails(NULL, results[0], setValue, setSaveAddress, clearSuggestions, checkFormValidation);
          setNonFormattedAddress(addresses?.addressLines[1]);
          return getLatLng(results[0]);
        })
        .then(data => {
          geoLocation.calculateGH({
            lat: parseFloat(data?.lat),
            lon: parseFloat(data?.lng),
            distanceFromCurrentLoc: boundingBoxSize,
            geoHashPrecision: geohashprecision,
          });
          setSaveAddress(prevState => ({
            ...prevState,
            latitude: data?.lat,
            longitude: data?.lng,
            isAddressSelected: true,
            isFlatNumberSelected: true,
          }));
        })
        .catch(errorMessage => {
          console.log('Error: ', errorMessage);
        });
    };

  const checkFormValidation = () => {
    return (
      addresses?.addressLines[1]?.length &&
      addresses?.city?.length &&
      addresses?.pinCode?.length &&
      addresses?.state?.length &&
      addresses?.deliveryOptions?.length &&
      (shouldEnableConfirmButton ? addresses?.addressLines[0]?.length : true) &&
      !isNameExist &&
      !isInvalidName
    );
  };

  const filterStores = store => {
    return (
      store?.id &&
      store?.location?.latitude &&
      store?.location?.longitude &&
      store?.availableServicesPerChannel?.filter(
        service => service?.channel === CHANNEL && service?.services?.disposition === DELIVERY_SERVICE,
      )
    );
  };

  const getAddressValue = () => {
    let addrValue = `${translateWithI18Next('Address')} 1`;
    const addressNameArray = savedAddresses.map(addr => addr.name);
    const nameArray = [...addressNameArray].filter(addr => addr.indexOf(translateWithI18Next('Address')) > -1);
    return getUniqueAddrName(nameArray, addrValue);
  };

  const getUniqueAddrName = (nameArray, name) => {
    let string = name;
    if (nameArray.includes(string)) {
      string = string.replace(/(\d+)+/g, (match, number) => parseInt(number) + 1);
      return getUniqueAddrName(nameArray, string);
    } else {
      return string;
    }
  };

  const getPayloadData = addressesInfo => {
    let data = {};
    let addressLines = ['', ''];
    addressLines[1] = addressesInfo.addressLines[1];
    addressLines[0] = getAndCheckIfOneTrue(addressesInfo?.floor, addressesInfo.addressLines[0]);
    data = {
      addressId: getValueOrEmptyString(addressesInfo?.id),
      addressName: getAndCheckIfOneTrue(addressesInfo.name, getAddressValue()),
      city: addressesInfo.city,
      postalCode: addressesInfo?.pinCode,
      state: addressesInfo.state,
      country: getValueOrEmptyString(addressesInfo.country),
      isPrimary: getCheckIfAnyOnePass(addressesInfo.isPrimary, savedAddresses.length < 1, false),
      location: {
        latitude: getAndCheckIfOneTrue(addressesInfo?.location?.latitude, addressesInfo?.latitude), //15.749100576992188,
        longitude: getAndCheckIfOneTrue(addressesInfo?.location?.longitude, addressesInfo?.longitude), //75.25417921693628
      },
      addressLines: addressLines,
      deliveryInstructions: addressesInfo?.deliveryInstructions,
      deliveryOptions: addressesInfo?.deliveryOptions,
    };
    return data;
  };

  const handleSubmit = isAddressFromOrderProcessing => {
    const data = getPayloadData(addresses);
    const validStores = allStores?.filter(filterStores);
    const driveTime = storeSearch?.maxDriveTimeMins;
    //Linear Distance
    const distanceInMtrs = getAndCheckIfOneTrue(storeSearch?.maxDeliveryDistanceMtrs, MAX_DELIVERY_DISTANCE);
    const sortedStoresList = geoLocation.checkAndProceed({
      stores: validStores,
      disposition: DELIVERY_SERVICE,
      distanceMetrics: storeSearch?.distanceMetrics,
      driveTime,
      distanceInMtrs,
      noOfStores: NO_OF_STORE_FOR_DELIVERY,
    });
    if (isAddressFromOrderProcessing) {
      updateSetOrderDetails({ savedAddressSucess: true });
    }
    if (sortedStoresList?.length || isAddressFromOrderProcessing) {
      const { customerId, phoneNumber } = AuthManager.getInstance();

      dispatch(
        addNewAddress({
          customerId,
          phoneNumber,
          tenantId,
          data,
        }),
      );
      handleClose();
      const addressToastMessage = getTernaryValue(
        modalType,
        translateWithI18Next(ADDRESS_UPDATED),
        translateWithI18Next(SAVED_DELIVERY_ADDRESS),
      );
      toastifyFn(addressToastMessage, '', 'favorites-added-toast');
    } else {
      setError(true);
    }
  };
  return (
    <AddressModal
      {...props}
      setValue={setValue}
      value={value}
      error={error}
      addresses={addresses}
      handleChange={handleChange}
      handleInput={handleInput}
      ready={ready}
      suggestionRef={suggestionRef}
      suggestions={suggestionsData}
      handleSelect={handleSelect}
      checkFormValidation={checkFormValidation}
      handleSubmit={handleSubmit}
      isNameExist={isNameExist}
      isInvalidName={isInvalidName}
      handleClose={handleClose}
      savedAddresses={savedAddresses}
      showSuggestions={showSuggestions}
      country={country}
      countryNameInDefaultLang={countryNameInDefaultLang}
      setSaveAddress={setSaveAddress}
      nonFormattedAddress={nonFormattedAddress}
      setNonFormattedAddress={setNonFormattedAddress}
    />
  );
};

export default AddressModalContainer;
